import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Wind,
  Activity,
  Droplets,
  HeartPulse,
  Bone,
  AlertOctagon,
  Award,
  CheckCircle,
  ChevronRight,
  Shield,
  Hand,
  Flame,
  HelpCircle,
  ArrowLeft,
} from "lucide-react";
import api from "../api/axios";
import { withBystanderAuth } from "../utils/bystanderAccess";
import StickyCallBar from "../components/shared/StickyCallBar";
import EmergencyLayout from "../components/layout/EmergencyLayout";
import { useLanguage } from "../contexts/LanguageContext";
import { RAH_VEER_FLOW_PAUSED } from "../config/pilot";

const DRABC_STEPS = [
  {
    id: "danger",
    icon: AlertTriangle,
    color: "bg-destructive",
    borderColor: "border-destructive/30",
    bgColor: "bg-destructive/10",
    actions: [
      "Look for traffic from all directions",
      "Check for fire or smoke nearby",
      "Look for fallen electrical wires",
      "Make the area safe if possible (turn on hazard lights, place warning signs)",
    ],
    actionsHi: [
      "सभी दिशाओं से यातायात देखें",
      "आस-पास आग या धुएं की जांच करें",
      "गिरे हुए बिजली के तारों की तलाश करें",
      "यदि संभव हो तो क्षेत्र को सुरक्षित बनाएं (हैजार्ड लाइट चालू करें)",
    ],
  },
  {
    id: "response",
    icon: Hand,
    color: "bg-orange-600",
    borderColor: "border-orange-200",
    bgColor: "bg-orange-50",
    actions: [
      "Tap both shoulders gently but firmly",
      "Speak loudly and clearly near both ears",
      "Check for any visible injuries",
      "If responsive, ask them to stay still and wait for help",
    ],
    actionsHi: [
      "दोनों कंधों को धीरे से लेकिन मजबूती से थपथपाएं",
      "दोनों कानों के पास जोर से और स्पष्ट रूप से बोलें",
      "किसी भी दिखाई देने वाली चोट की जांच करें",
      "यदि प्रतिक्रिया दे रहे हैं, तो उन्हें स्थिर रहने और मदद की प्रतीक्षा करने के लिए कहें",
    ],
  },
  {
    id: "airway",
    icon: Wind,
    color: "bg-teal-600",
    borderColor: "border-teal-200",
    bgColor: "bg-teal-50",
    actions: [
      "Place one hand on forehead",
      "Lift chin with two fingers of other hand",
      "Tilt head back gently",
      "Look inside mouth for obstructions - remove carefully",
    ],
    actionsHi: [
      "एक हाथ माथे पर रखें",
      "दूसरे हाथ की दो उंगलियों से ठुड्डी उठाएं",
      "सिर को धीरे से पीछे झुकाएं",
      "मुंह के अंदर रुकावट देखें - सावधानी से हटाएं",
    ],
  },
  {
    id: "breathing",
    icon: Activity,
    color: "bg-blue-600",
    borderColor: "border-blue-200",
    bgColor: "bg-blue-50",
    actions: [
      "LOOK: Watch the chest for 10 seconds",
      "LISTEN: Put your ear near their mouth",
      "FEEL: Check for air flow on your cheek",
      "If NOT breathing → Start CPR immediately",
    ],
    actionsHi: [
      "देखें: 10 सेकंड तक छाती देखें",
      "सुनें: अपना कान उनके मुंह के पास रखें",
      "महसूस करें: अपने गाल पर हवा का प्रवाह जांचें",
      "यदि सांस नहीं चल रही → तुरंत सीपीआर शुरू करें",
    ],
  },
  {
    id: "circulation",
    icon: Droplets,
    color: "bg-destructive",
    borderColor: "border-destructive/30",
    bgColor: "bg-destructive/10",
    actions: [
      "Scan entire body for bleeding wounds",
      "Apply firm pressure with clean cloth",
      "DO NOT remove the cloth - add more layers",
      "Elevate bleeding limbs above heart level if possible",
    ],
    actionsHi: [
      "पूरे शरीर में रक्तस्राव के घाव की जांच करें",
      "साफ कपड़े से मजबूत दबाव लगाएं",
      "कपड़ा न हटाएं - और परतें जोड़ें",
      "यदि संभव हो तो रक्तस्राव वाले अंगों को हृदय स्तर से ऊपर उठाएं",
    ],
  },
];

const INTERVENTION_CARDS = [
  {
    id: "cpr",
    icon: HeartPulse,
    color: "bg-destructive",
    steps: [
      "Place the heel of your hand on the CENTER of the chest",
      "Interlock your other hand on top",
      "Push HARD and FAST - at least 5cm deep",
      "30 compressions, then 2 rescue breaths",
      "Rate: 100-120 compressions per minute",
      "Continue until ambulance arrives",
    ],
    stepsHi: [
      "अपने हाथ की एड़ी को छाती के बीच में रखें",
      "दूसरा हाथ ऊपर इंटरलॉक करें",
      "जोर से और तेज दबाएं - कम से कम 5 सेमी गहरा",
      "30 दबाव, फिर 2 बचाव श्वास",
      "गति: प्रति मिनट 100-120 दबाव",
      "एम्बुलेंस आने तक जारी रखें",
    ],
  },
  {
    id: "bleeding",
    icon: Droplets,
    color: "bg-orange-600",
    steps: [
      "Apply DIRECT, CONTINUOUS pressure with clean cloth",
      "Do NOT remove the cloth - add more layers",
      "If limb bleeding, elevate above heart level",
      "For arterial bleeding (bright red, spurting): Apply maximum pressure",
      "Improvise bandage with dupatta, shirt, or any clean fabric",
    ],
    stepsHi: [
      "साफ कपड़े से सीधा, निरंतर दबाव लगाएं",
      "कपड़ा न हटाएं - और परतें जोड़ें",
      "यदि अंग से रक्तस्राव हो, हृदय स्तर से ऊपर उठाएं",
      "धमनी रक्तस्राव (चमकदार लाल) के लिए: अधिकतम दबाव लगाएं",
      "दुपट्टा, शर्ट या किसी साफ कपड़े से पट्टी बनाएं",
    ],
  },
  {
    id: "fracture",
    icon: Bone,
    color: "bg-teal-600",
    steps: [
      "DO NOT try to straighten or move the injured limb",
      "Support the limb in the position you find it",
      "Use a wooden block, umbrella, or rolled newspaper as splint",
      "Tie gently above and below the fracture site",
      "Check circulation in fingers/toes below the injury",
    ],
    stepsHi: [
      "घायल अंग को सीधा करने या हिलाने की कोशिश न करें",
      "जिस स्थिति में मिले उसी स्थिति में अंग को सहारा दें",
      "लकड़ी के ब्लॉक, छतरी या मुड़े हुए अखबार को स्प्लिंट के रूप में उपयोग करें",
      "फ्रैक्चर स्थल के ऊपर और नीचे धीरे से बांधें",
      "चोट के नीचे उंगलियों/पैर की उंगलियों में रक्त संचार जांचें",
    ],
  },
];

const EMERGENCY_TYPES = [
  {
    id: "unconscious",
    icon: Hand,
    color: "bg-orange-600",
    labelEn: "Unconscious",
    labelHi: "बेहोश",
    checklistEn: [
      "Call 112 / 108 if not already done",
      "Check Danger — is the scene safe?",
      "Tap shoulders and shout — any response?",
      "Open airway (head tilt, chin lift) unless neck injury suspected",
      "Check breathing for 10 seconds",
      "If not breathing → start CPR now",
      "If breathing → recovery position, keep watching",
    ],
    checklistHi: [
      "यदि पहले नहीं किया तो 112 / 108 पर कॉल करें",
      "खतरा जांचें — जगह सुरक्षित है?",
      "कंधे थपथपाएं और चिल्लाएं — कोई प्रतिक्रिया?",
      "वायुमार्ग खोलें (सिर झुकाएं, ठुड्डी उठाएं) जब तक गर्दन की चोट का संदेह न हो",
      "10 सेकंड तक सांस जांचें",
      "यदि सांस नहीं → तुरंत सीपीआर शुरू करें",
      "यदि सांस चल रही → रिकवरी पोजीशन, निगरानी रखें",
    ],
    interventions: ["cpr"],
    drabcIds: ["danger", "response", "airway", "breathing"],
  },
  {
    id: "not_breathing",
    icon: HeartPulse,
    color: "bg-destructive",
    labelEn: "Not breathing",
    labelHi: "सांस नहीं चल रही",
    checklistEn: [
      "Call 112 / 108 — say they are not breathing",
      "Ensure scene is safe",
      "Open airway",
      "Start CPR immediately (30 compressions + 2 breaths)",
      "Continue until help arrives or they start breathing",
    ],
    checklistHi: [
      "112 / 108 पर कॉल करें — बताएं कि सांस नहीं चल रही",
      "जगह सुरक्षित सुनिश्चित करें",
      "वायुमार्ग खोलें",
      "तुरंत सीपीआर शुरू करें (30 दबाव + 2 श्वास)",
      "मदद आने या सांस शुरू होने तक जारी रखें",
    ],
    interventions: ["cpr"],
    drabcIds: ["danger", "airway", "breathing", "circulation"],
  },
  {
    id: "bleeding",
    icon: Droplets,
    color: "bg-orange-700",
    labelEn: "Bleeding",
    labelHi: "रक्तस्राव",
    checklistEn: [
      "Call 112 / 108 for heavy bleeding",
      "Protect yourself — use gloves or a barrier if available",
      "Apply direct continuous pressure with clean cloth",
      "Do not remove soaked cloth — add layers",
      "Elevate the limb if no fracture is suspected",
      "Keep the person warm and calm until help arrives",
    ],
    checklistHi: [
      "भारी रक्तस्राव पर 112 / 108 कॉल करें",
      "अपनी सुरक्षा — दस्ताने या बाधा का उपयोग करें",
      "साफ कपड़े से सीधा निरंतर दबाव लगाएं",
      "भीगा कपड़ा न हटाएं — परतें जोड़ें",
      "फ्रैक्चर संदेह न हो तो अंग ऊपर उठाएं",
      "मदद आने तक व्यक्ति को गर्म और शांत रखें",
    ],
    interventions: ["bleeding"],
    drabcIds: ["danger", "circulation"],
  },
  {
    id: "broken_bone",
    icon: Bone,
    color: "bg-teal-700",
    labelEn: "Broken bone",
    labelHi: "हड्डी टूटी",
    checklistEn: [
      "Call 112 / 108 if the person cannot move safely",
      "Do not straighten or force the limb",
      "Support in the position found",
      "Immobilize with a soft splint (rolled cloth, umbrella)",
      "Check fingers/toes for colour and sensation",
      "Treat for shock — keep warm, reassure",
    ],
    checklistHi: [
      "यदि सुरक्षित रूप से हिल न सकें तो 112 / 108 कॉल करें",
      "अंग को सीधा या जबरदस्ती न करें",
      "जिस स्थिति में मिले उसी में सहारा दें",
      "नरम स्प्लिंट से स्थिर करें (कपड़ा, छतरी)",
      "उंगलियों का रंग और संवेदना जांचें",
      "शॉक से बचाव — गर्म रखें, आश्वस्त करें",
    ],
    interventions: ["fracture"],
    drabcIds: ["danger", "response", "circulation"],
  },
  {
    id: "fire",
    icon: Flame,
    color: "bg-amber-600",
    labelEn: "Fire-related",
    labelHi: "आग संबंधी",
    checklistEn: [
      "Get yourself and the person to a safer place first",
      "Call 112 / 101 — report fire and injuries",
      "Stop, drop, and roll if clothing is on fire",
      "Cool burns with clean running water for 20 minutes if available",
      "Do not apply ice, oil, or toothpaste",
      "Cover loosely with clean cloth; watch breathing",
    ],
    checklistHi: [
      "पहले खुद को और व्यक्ति को सुरक्षित जगह ले जाएं",
      "112 / 101 पर कॉल करें — आग और चोट बताएं",
      "कपड़ों में आग हो तो रुकें, गिरें, लोटें",
      "जलन पर 20 मिनट साफ बहता पानी (यदि उपलब्ध)",
      "बर्फ, तेल या टूथपेस्ट न लगाएं",
      "साफ कपड़े से ढीला ढकें; सांस पर नज़र रखें",
    ],
    interventions: ["cpr", "bleeding"],
    drabcIds: ["danger", "response", "breathing"],
  },
  {
    id: "other",
    icon: HelpCircle,
    color: "bg-slate-700",
    labelEn: "Other / Full check",
    labelHi: "अन्य / पूरी जांच",
    checklistEn: [
      "Call 112 / 108",
      "Use the full ABCD check below",
      "Open the intervention that matches what you see",
    ],
    checklistHi: [
      "112 / 108 पर कॉल करें",
      "नीचे पूरी ABCD जांच करें",
      "जो दिखे उसके अनुसार हस्तक्षेप खोलें",
    ],
    interventions: ["cpr", "bleeding", "fracture"],
    drabcIds: ["danger", "response", "airway", "breathing", "circulation"],
    showFullDrabc: true,
  },
];

const TYPE_ALIASES = {
  unconscious: "unconscious",
  not_breathing: "not_breathing",
  "not-breathing": "not_breathing",
  bleeding: "bleeding",
  broken_bone: "broken_bone",
  fracture: "broken_bone",
  fire: "fire",
  other: "other",
  abcd: "other",
  drabc: "other",
};

function ChecklistPanel({ type, lang, completed, onToggle, t }) {
  const items = lang === "hi" ? type.checklistHi : type.checklistEn;
  return (
    <div className="space-y-2 mb-4">
      <h2 className="text-base font-bold text-foreground m-0">
        {lang === "hi" ? type.labelHi : type.labelEn} — {t("rescue.primarySurveyTitle") || "What to do now"}
      </h2>
      <ol className="list-none p-0 m-0 space-y-2">
        {items.map((text, idx) => {
          const stepId = `${type.id}-${idx}`;
          const done = completed.includes(stepId);
          return (
            <li key={stepId}>
              <button
                type="button"
                onClick={() => onToggle(stepId)}
                className={`w-full flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${
                  done ? "bg-emerald-50 border-emerald-200" : "bg-card border-border"
                }`}
              >
                <span
                  className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                    done ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {done ? <CheckCircle size={14} /> : idx + 1}
                </span>
                <span className={`text-sm ${done ? "text-emerald-900" : "text-foreground"}`}>{text}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function InterventionList({ cards, lang, t, activeIntervention, setActiveIntervention }) {
  return (
    <div className="space-y-2 mb-4">
      <h2 className="text-lg font-bold text-foreground mb-1">{t("rescue.criticalInterventions")}</h2>
      <p className="text-sm text-muted-foreground mb-3">{t("rescue.goldenHourSubtitle")}</p>
      {cards.map((card, cardIdx) => {
        const Icon = card.icon;
        const isOpen = activeIntervention === card.id || (activeIntervention === null && cardIdx === 0);
        return (
          <div key={card.id} className="bg-card rounded-lg border border-border shadow-card shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setActiveIntervention(isOpen ? null : card.id)}
              className={`w-full px-3 py-3 flex items-center gap-3 text-left transition-colors ${
                isOpen ? `${card.color} text-white` : "hover:bg-muted"
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isOpen ? "bg-white/20" : card.color}`}>
                <Icon size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-bold ${isOpen ? "text-white" : "text-foreground"}`}>
                  {t(`rescue.${card.id}Title`)}
                </p>
              </div>
              <ChevronRight size={18} className={`shrink-0 transition-transform ${isOpen ? "rotate-90 text-white" : "text-muted-foreground"}`} />
            </button>
            {isOpen && (
              <div className="px-3 py-3 animate-fade-in">
                <p className="text-xs text-foreground leading-relaxed mb-2">{t(`rescue.${card.id}Desc`)}</p>
                <div className="space-y-2">
                  {(lang === "hi" ? card.stepsHi : card.steps).map((s, sIdx) => (
                    <div key={sIdx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 text-xs font-bold text-muted-foreground">
                        {sIdx + 1}
                      </div>
                      <p className="text-sm text-foreground">{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const GuidedRescuePage = () => {
  const { qrId } = useParams();
  const id = qrId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, lang } = useLanguage();
  const reportId = searchParams.get("reportId");
  const typeParam = searchParams.get("type");

  const initialType = TYPE_ALIASES[String(typeParam || "").toLowerCase()] || null;

  const [selectedTypeId, setSelectedTypeId] = useState(initialType);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [checklistDone, setChecklistDone] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [activeIntervention, setActiveIntervention] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [sessionStartTime] = useState(Date.now());
  const [sessionId, setSessionId] = useState(null);
  const [reportEmergencyType, setReportEmergencyType] = useState(null);
  const stepRefs = useRef([]);

  const selectedType = useMemo(
    () => EMERGENCY_TYPES.find((entry) => entry.id === selectedTypeId) || null,
    [selectedTypeId],
  );

  useEffect(() => {
    if (initialType) setSelectedTypeId(initialType);
  }, [initialType]);

  useEffect(() => {
    const createSession = async () => {
      if (!reportId) return;
      try {
        const res = await api.post(
          `/emergency-report/${reportId}/rescue-session`,
          {},
          withBystanderAuth({}, reportId),
        );
        if (res.data?.id) setSessionId(res.data.id);
        if (res.data?.emergencyType) setReportEmergencyType(res.data.emergencyType);
      } catch (err) {
        console.error("Failed to create rescue session:", err);
      }
    };
    createSession();
  }, [reportId]);

  const selectType = (typeId) => {
    setSelectedTypeId(typeId);
    setChecklistDone([]);
    setCompletedSteps([]);
    setActiveStep(0);
    setActiveIntervention(null);
    const next = new URLSearchParams(searchParams);
    next.set("type", typeId);
    setSearchParams(next, { replace: true });
  };

  const clearType = () => {
    setSelectedTypeId(null);
    setChecklistDone([]);
    setCompletedSteps([]);
    setActiveStep(0);
    setActiveIntervention(null);
    const next = new URLSearchParams(searchParams);
    next.delete("type");
    setSearchParams(next, { replace: true });
  };

  const toggleChecklist = (stepId) => {
    setChecklistDone((prev) => (prev.includes(stepId) ? prev.filter((id) => id !== stepId) : [...prev, stepId]));
  };

  const markStepComplete = async (stepId) => {
    if (completedSteps.includes(stepId)) return;
    const updated = [...completedSteps, stepId];
    setCompletedSteps(updated);

    const steps = selectedType?.showFullDrabc
      ? DRABC_STEPS
      : DRABC_STEPS.filter((s) => (selectedType?.drabcIds || []).includes(s.id));
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      stepRefs.current[activeStep + 1]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (sessionId) {
      const rewardEligible = RAH_VEER_FLOW_PAUSED ? false : updated.length + checklistDone.length >= 3;
      const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
      try {
        await api.put(
          `/emergency-report/rescue-session/${sessionId}`,
          {
            stepsCompleted: updated,
            rewardEligible,
            durationSeconds: duration,
          },
          withBystanderAuth({}, reportId),
        );
      } catch (err) {
        console.error("Failed to update session:", err);
      }
    }
  };

  const orderedInterventions = useMemo(() => {
    if (!selectedType) return INTERVENTION_CARDS;
    const order = selectedType.interventions;
    return [...INTERVENTION_CARDS]
      .filter((card) => order.includes(card.id))
      .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [selectedType]);

  const visibleDrabc = useMemo(() => {
    if (!selectedType) return [];
    if (selectedType.showFullDrabc) return DRABC_STEPS;
    return [];
  }, [selectedType]);

  const rewardEligible =
    !RAH_VEER_FLOW_PAUSED && completedSteps.length + checklistDone.length >= 3;

  const publicUrl = `${window.location.origin}/${id}/public`;
  const whatsappMsg = encodeURIComponent(
    `🚨 Emergency alert! Someone needs help. Scan their QR code for medical info: ${publicUrl}`,
  );

  const emergencyTypeBanner = {
    medical: { text: t("rescue.typeMedical"), bg: "bg-destructive" },
    accident: { text: t("rescue.typeAccident"), bg: "bg-orange-500" },
    fire: { text: t("rescue.typeFire"), bg: "bg-amber-500" },
  };

  const handleBack = () => {
    if (activeIntervention) {
      setActiveIntervention(null);
      return;
    }
    if (selectedTypeId) {
      clearType();
      return;
    }
    navigate(`/${id}/public`);
  };

  return (
    <EmergencyLayout
      title={t("rescue.title")}
      subtitle={t("rescue.subtitle")}
      showBack
      onBack={handleBack}
      headerVariant="emergency"
      footer={<StickyCallBar />}
      headerRight={
        selectedType ? (
          <span className="text-xs font-semibold text-white/90 border border-white/25 rounded-lg px-2 py-1">
            {lang === "hi" ? selectedType.labelHi : selectedType.labelEn}
          </span>
        ) : null
      }
    >
      <div className="pb-4">
        {reportEmergencyType && emergencyTypeBanner[reportEmergencyType] && (
          <div className={`${emergencyTypeBanner[reportEmergencyType].bg} rounded-lg p-3 mb-2 shadow-md`}>
            <p className="text-sm font-bold text-white text-center">{emergencyTypeBanner[reportEmergencyType].text}</p>
          </div>
        )}

        <div className="bg-destructive rounded-lg p-3 mb-2 shadow-card shadow-destructive/20">
          <div className="flex items-center gap-2 text-white">
            <AlertOctagon size={18} className="shrink-0" />
            <p className="text-sm font-bold">{t("rescue.neckWarning")}</p>
          </div>
        </div>

        <a
          href={`https://wa.me/?text=${whatsappMsg}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold text-sm py-3 rounded-lg mb-3 shadow-md transition-colors duration-150"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          {t("rescue.whatsappNotify")}
        </a>

        {!selectedType && (
          <div className="mb-4">
            <h2 className="text-base font-bold text-foreground mb-1">
              {lang === "hi" ? "क्या हो रहा है?" : "What is happening?"}
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              {lang === "hi"
                ? "प्रकार चुनें — छोटी जाँच सूची तुरंत खुलेगी।"
                : "Choose a type — a short checklist opens right away."}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {EMERGENCY_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => selectType(type.id)}
                    className={`${type.color} text-white rounded-xl p-4 min-h-[5.5rem] flex flex-col items-start justify-between gap-2 text-left shadow-md active:scale-[0.98] transition-transform`}
                  >
                    <Icon size={22} aria-hidden />
                    <span className="text-sm font-bold leading-tight">
                      {lang === "hi" ? type.labelHi : type.labelEn}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {selectedType && (
          <>
            <button
              type="button"
              onClick={clearType}
              className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
            >
              <ArrowLeft size={16} />
              {lang === "hi" ? "दूसरा प्रकार चुनें" : "Choose another type"}
            </button>

            <ChecklistPanel
              type={selectedType}
              lang={lang}
              completed={checklistDone}
              onToggle={toggleChecklist}
              t={t}
            />

            <InterventionList
              cards={orderedInterventions}
              lang={lang}
              t={t}
              activeIntervention={activeIntervention}
              setActiveIntervention={setActiveIntervention}
            />

            {visibleDrabc.length > 0 && (
              <div className="mb-4">
                <h2 className="text-base font-bold text-foreground mb-0.5">{t("rescue.primarySurveyTitle")}</h2>
                <p className="text-xs text-muted-foreground mb-3">{t("rescue.primarySurveySubtitle")}</p>
                <div className="space-y-2">
                  {visibleDrabc.map((drabcStep, idx) => {
                    const Icon = drabcStep.icon;
                    const isCompleted = completedSteps.includes(drabcStep.id);
                    const isActive = idx === activeStep && !isCompleted;
                    return (
                      <div
                        key={drabcStep.id}
                        ref={(el) => {
                          stepRefs.current[idx] = el;
                        }}
                        className={`rounded-lg border overflow-hidden transition-colors duration-150 ${
                          isCompleted
                            ? "bg-emerald-50 border-emerald-200"
                            : isActive
                              ? `${drabcStep.bgColor} ${drabcStep.borderColor} border-2 shadow-md`
                              : "bg-card border-border opacity-60"
                        }`}
                      >
                        <div className={`px-3 py-3 ${isActive ? `${drabcStep.color} text-white` : ""}`}>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                                isCompleted ? "bg-emerald-600" : isActive ? "bg-white/20" : "bg-muted"
                              }`}
                            >
                              {isCompleted ? (
                                <CheckCircle size={20} className="text-white" />
                              ) : (
                                <Icon size={20} className={isActive ? "text-white" : "text-muted-foreground"} />
                              )}
                            </div>
                            <div className="flex-1">
                              <p
                                className={`text-sm font-bold ${
                                  isActive ? "text-white" : isCompleted ? "text-emerald-800" : "text-foreground"
                                }`}
                              >
                                {"DRABC"[idx]} - {t(`rescue.${drabcStep.id}`)}
                              </p>
                            </div>
                          </div>
                        </div>
                        {(isActive || isCompleted) && (
                          <div className="px-3 py-3">
                            <p className="text-xs text-foreground leading-relaxed mb-3">
                              {t(`rescue.${drabcStep.id}Desc`)}
                            </p>
                            {(() => {
                              const allActions = lang === "hi" ? drabcStep.actionsHi : drabcStep.actions;
                              const expanded = expandedSteps[drabcStep.id];
                              const visible = expanded ? allActions : allActions.slice(0, 1);
                              return (
                                <>
                                  <div className="space-y-1.5 mb-2">
                                    {visible.map((action, aIdx) => (
                                      <div key={aIdx} className="flex items-start gap-2">
                                        <ChevronRight size={14} className="text-muted-foreground shrink-0 mt-0.5" />
                                        <p className="text-sm text-foreground">{action}</p>
                                      </div>
                                    ))}
                                  </div>
                                  {allActions.length > 1 && (
                                    <button
                                      type="button"
                                      className="text-xs font-semibold text-primary mb-3 underline-offset-2 hover:underline"
                                      onClick={() =>
                                        setExpandedSteps((prev) => ({
                                          ...prev,
                                          [drabcStep.id]: !prev[drabcStep.id],
                                        }))
                                      }
                                    >
                                      {expanded ? t("rescue.showLess") : t("rescue.showAllSteps")}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                            {!isCompleted && (
                              <button
                                type="button"
                                onClick={() => markStepComplete(drabcStep.id)}
                                className={`w-full flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg transition-colors duration-150 ${drabcStep.color}`}
                              >
                                <CheckCircle size={18} />
                                {t("rescue.markAsDone")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!RAH_VEER_FLOW_PAUSED && (
              <div
                className={`rounded-lg p-3 mb-3 border transition-colors duration-150 ${
                  rewardEligible ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Award size={16} className={rewardEligible ? "text-emerald-600" : "text-amber-600"} />
                  <p className={`text-xs font-bold ${rewardEligible ? "text-emerald-800" : "text-amber-800"}`}>
                    {rewardEligible
                      ? t("rescue.rewardBanner")
                      : t("rescue.completeMoreSteps").replace(
                          "{{n}}",
                          Math.max(1, 3 - (completedSteps.length + checklistDone.length)),
                        )}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        <div className="bg-card rounded-lg border border-border shadow-card p-4 text-center mt-4">
          <Shield size={16} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground leading-relaxed">{t("rescue.disclaimer")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("rescue.poweredBy")}</p>
        </div>
      </div>
    </EmergencyLayout>
  );
};

export default GuidedRescuePage;
